import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminConsoleSession } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Filter } from "@/ui/Filter";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { useFilters } from "@/features/admin/useFilters";
import { absoluteTime, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/console")({
  component: ConsoleScreen,
});

/**
 * Who opened a shell into whose container.
 *
 * # WHY THIS IS HERE
 *
 * A console session is the most invasive thing anybody can do on this platform:
 * a live shell inside a customer's running container, with their environment
 * and their data. Every fact needed to hold somebody to one has been recorded
 * since consoles were built — who asked, the exact command, the stated reason,
 * when it attached and when it closed.
 *
 * Nothing read any of it. The only code in the staff package that touched
 * console_sessions deleted rows from it, when deleting a user. A record nobody
 * can read holds nobody to anything.
 */
function ConsoleScreen() {
  const filters = useFilters();
  const staffOnly = filters.on("staff");
  const setStaffOnly = (value: boolean) => filters.toggle("staff", value);

  const sessions = useQuery({
    queryKey: ["platform-console", staffOnly],
    queryFn: ({ signal }) =>
      api.get<{ sessions: AdminConsoleSession[] | null }>(
        "/admin/console-sessions",
        { staff_only: staffOnly || undefined, limit: 200 },
        signal,
      ),
    refetchInterval: 30000,
  });

  const list = sessions.data?.sessions ?? [];
  const live = list.filter((s) => !s.closed_at);
  const byStaff = list.filter((s) => s.requested_by_staff);

  return (
    <div className="flex flex-col gap-4">
      {/* Open shells, said first. A session that is attached right now is
          somebody inside a customer's container at this moment, which is a
          different kind of fact from a list of what happened last week. */}
      {live.length > 0 && (
        <div className="rounded-xl border border-[color-mix(in_oklab,_var(--warning)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--warning)]">
            {live.length === 1 ? "One shell is open" : `${live.length} shells are open`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            {live
              .map((s) => `${s.requested_by_email || "someone"} in ${s.service_name || s.service_id}`)
              .join(", ")}
          </p>
        </div>
      )}

      <Panel
        toolbar={
          <Filter
            label="Drumlin staff only"
            count={byStaff.length}
            active={staffOnly}
            onClick={() => setStaffOnly(!staffOnly)}
          />
        }
        padded={false}
      >
        {sessions.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={5} columns={6} />
          </div>
        ) : sessions.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title={staffOnly ? "No staff has opened a shell" : "No shell sessions"}
              description={
                staffOnly
                  ? "Nobody from Drumlin has opened a shell into a customer's container."
                  : "Nobody has opened one."
              }
            />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[21%]">Who</Th>
                  <Th width="w-[20%]">Into</Th>
                  <Th width="w-[16%]">Command</Th>
                  <Th width="w-[19%]">Stated reason</Th>
                  <Th width="w-[12%]">State</Th>
                  <Th width="w-[12%]" align="right">
                    Opened
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <Tr
                    key={s.id}
                    className={
                      s.requested_by_staff ? "bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]" : undefined
                    }
                  >
                    <Td clip title={s.requested_by_email || s.requested_by}>
                      {s.requested_by_staff && (
                        <Chip tone="live" className="mr-1.5">
                          Staff
                        </Chip>
                      )}
                      <Link
                        to="/admin/users/$userId"
                        params={{ userId: s.requested_by }}
                        className="hover:text-[var(--primary)]"
                      >
                        {s.requested_by_email || s.requested_by}
                      </Link>
                    </Td>
                    <Td
                      clip
                      title={`${s.organisation_name} · ${s.project_name ?? ""} · ${s.service_name ?? s.service_id}`}
                    >
                      <Link
                        to="/admin/$orgId"
                        params={{ orgId: s.organisation_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {s.service_name || s.service_id}
                      </Link>
                      <span className="ml-1.5 text-[13px] text-[var(--text-muted)]">
                        {s.organisation_name}
                      </span>
                    </Td>
                    <Td clip className="mono text-[13px]" title={s.command.join(" ")}>
                      {s.command.join(" ") || "—"}
                    </Td>
                    {/* The reason the person gave. An empty one is itself worth
                        seeing, which is why it says so rather than showing a
                        dash that reads like missing data. */}
                    <Td clip title={s.reason || undefined}>
                      <span
                        className={
                          s.reason ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]"
                        }
                      >
                        {s.reason || "none given"}
                      </span>
                    </Td>
                    <Td>
                      <Chip tone={s.closed_at ? undefined : "warn"}>
                        {s.closed_at ? "closed" : s.state}
                      </Chip>
                    </Td>
                    <Td align="right" title={absoluteTime(s.created_at)}>
                      {shortTime(s.created_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}
